const numero = v => Number(v || 0);
const soma = (itens, campo) => itens.reduce((total, i) => total + numero(i[campo]), 0);
const unicos = (itens, campo) => [...new Set(itens.map(i => i[campo]))];
const datas = (itens, campo) => itens.map(i => i[campo]).filter(Boolean).sort();

export function resumirExecucao(itens, processos = []) {
  // Each detail carries hours for its own installed solution. Shared report and
  // meeting IDs count once, while their distinct solution-hour allocations add up.
  const registros = [...new Map(itens.map(i => [i.chaveRegistro || `cadastro:${i.municipioSolucaoId}`, i])).values()];
  const encontros = registros.flatMap(i => i.encontrosDetalhes || []);
  const relatorios = registros.flatMap(i => i.relatoriosDetalhes || []);
  const idsProcessos = new Set(relatorios.flatMap(r => r.processoIds || []));
  const vinculados = processos.filter(p => idsProcessos.has(p.processoId));
  const validos = new Set(vinculados.filter(p => p.statusId !== 2).map(p => p.processoId));
  const inicio = datas(registros, "inicio");
  const fim = datas(registros, "fim");
  const previstas = soma(registros, "horasPrevistas");
  const agendadas = soma(encontros, "horas");
  return {
    previstas, agendadas, executadas: soma(registros, "horasExecutadas"),
    saldo: previstas - agendadas, horasRelatorios: soma(relatorios, "horas"),
    horasEmProcessos: soma(relatorios.filter(r => (r.processoIds || []).some(id => validos.has(id))), "horas"),
    encontros: unicos(encontros, "encontroId").length,
    relatorios: unicos(relatorios, "relatorioId").length,
    processos: vinculados.length, inicio: inicio[0] || null, fim: fim[fim.length - 1] || null,
    datasIncompletas: registros.some(i => !i.inicio || !i.fim),
    encontrosDetalhes: consolidarDetalhes(encontros, "encontroId"),
    relatoriosDetalhes: consolidarDetalhes(relatorios, "relatorioId"),
    processosDetalhes: vinculados,
  };
}

function consolidarDetalhes(dados, chave) {
  const mapa = new Map();
  dados.forEach(item => {
    const anterior = mapa.get(item[chave]);
    if (!anterior) mapa.set(item[chave], { ...item });
    else anterior.horas = numero(anterior.horas) + numero(item.horas);
  });
  return [...mapa.values()];
}

export function montarExplorador(itens, contratos, modo = "contrato", ordemContrato = "municipio") {
  const porContrato = new Map(contratos.map(c => [c.contratoId, c]));
  const processos = contratos.flatMap(c => c.processos || []);
  const sequencia = modo === "municipio" ? ["municipio", "contrato", "eixo", "solucao"]
    : modo === "eixo" ? ["eixo", "solucao", "municipio", "contrato"]
    : ordemContrato === "eixo" ? ["contrato", "eixo", "solucao", "municipio"]
    : ["contrato", "municipio", "eixo", "solucao"];
  function identidade(item, tipo) {
    if (tipo === "contrato") return item.contratoId || "sem-contrato";
    if (tipo === "municipio") return item.municipioId;
    if (tipo === "eixo") return item.grupoSolucaoId || item.grupoSolucao || "sem-eixo";
    return item.solucaoChave || item.solucaoId || item.solucao;
  }
  function montar(rows, nivel, prefixo) {
    if (nivel === sequencia.length) return [];
    const tipo = sequencia[nivel], grupos = new Map();
    rows.forEach(item => {
      const id = identidade(item, tipo);
      if (!grupos.has(id)) grupos.set(id, []);
      grupos.get(id).push(item);
    });
    // An unallocated consultant contract still has capacity and payment history.
    if (nivel === 0 && tipo === "contrato") contratos.forEach(c => { if (!grupos.has(c.contratoId)) grupos.set(c.contratoId, []); });
    return [...grupos].map(([id, grupo]) => {
      const item = grupo[0] || {}, contrato = tipo === "contrato" ? porContrato.get(id) : null;
      const nome = tipo === "contrato" ? contrato ? `Contrato ${contrato.numeroContrato || "S/N"} · ${contrato.consultor}`
        : id === "sem-contrato" ? "Sem contrato de consultor vinculado" : `Contrato #${id} · ${item.consultor || "Consultor não informado"}`
        : tipo === "municipio" ? item.municipio : tipo === "eixo" ? item.grupoSolucao : item.solucaoRaiz || item.solucao;
      const chave = `${prefixo}/${tipo}:${id}`;
      return { chave, tipo, nome, contrato, itens: grupo, resumo: resumirExecucao(grupo, processos), filhos: montar(grupo, nivel + 1, chave) };
    }).sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
  }
  return montar(itens, 0, modo);
}

export function referenciasMunicipais(itens, propostas) {
  const ids = new Set(itens.flatMap(i => [...(i.propostaIds || []), ...(i.propostaOrigemIds || [])]));
  const origens = new Set(itens.flatMap(i => i.propostaOrigemIds || []));
  const cidades = new Set(itens.map(i => i.municipioId));
  return propostas.filter(p => ids.has(p.propostaId) && cidades.has(p.municipioId))
    .map(p => ({ ...p, origemExata: origens.has(p.propostaId) }));
}
